import { FULFILLMENT_TOAST_MS } from '@/pages/fulfillment/components/toastTiming';
import '@/pages/fulfillment/components/fulfillmentToast.css';

/**
 * Spec [G-2]/[E-46]: one reused top-right element, timer reset on every message. A success leaves on the clock every
 * brief notification keeps; a failure stays until it is closed, like a refusal in the stack (SSD-7705).
 */
export const GTOAST_HIDE_MS = FULFILLMENT_TOAST_MS;

export interface GlobalToastOptions {
  subtext?: string;
  failed?: boolean;
}

const ELEMENT_ID = 'gtoast';
const STYLE_ID = 'gtoast-style';

/*
 * The card itself is the stack's (.ff-toast in fulfillmentToast.css); only its place is set here. It sits over the
 * stack, which shares the corner, so a standing refusal card cannot hide it.
 */
const STYLE = `
#${ELEMENT_ID} { position: fixed; top: 18px; right: 18px; z-index: 2001; }
#${ELEMENT_ID} .gtoast-sub { margin-top: 2px; padding-left: 0; }
`;

/** The stack's close mark (UltimateIcons CloseIcon), drawn without React. */
const CLOSE_ICON =
  '<svg viewBox="0 0 24 24" class="ff-icon" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6 6 18" /></svg>';

let hideTimer: ReturnType<typeof setTimeout> | null = null;

const ensureElement = (): HTMLElement => {
  if (!document.getElementById(STYLE_ID)) {
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = STYLE;
    document.head.appendChild(style);
  }
  let element = document.getElementById(ELEMENT_ID);
  if (!element) {
    element = document.createElement('div');
    element.id = ELEMENT_ID;
    element.setAttribute('role', 'status');
    element.setAttribute('aria-live', 'polite');
    document.body.appendChild(element);
  }
  return element;
};

/** The latest message wins: the single element is rewritten in place and its hide timer restarts. */
export const showGlobalToast = (text: string, options: GlobalToastOptions = {}): void => {
  const element = ensureElement();
  const failed = options.failed === true;
  element.className = `ff-global-toast ff-toast ${failed ? 'ff-toast-error bad' : 'ff-toast-success'}`;
  element.replaceChildren();
  const body = document.createElement('div');
  body.className = 'ff-toast-body';
  const line = document.createElement('div');
  line.className = 'ff-toast-text gtoast-text';
  line.textContent = text;
  body.appendChild(line);
  if (options.subtext) {
    const sub = document.createElement('div');
    sub.className = 'ff-toast-details gtoast-sub';
    sub.textContent = options.subtext;
    body.appendChild(sub);
  }
  element.appendChild(body);
  element.hidden = false;
  if (hideTimer) {
    clearTimeout(hideTimer);
    hideTimer = null;
  }
  if (failed) {
    // Something did not happen: it waits for the operator, who closes it here.
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'ff-toast-x';
    close.setAttribute('aria-label', `Dismiss ${text}`);
    close.innerHTML = CLOSE_ICON;
    close.addEventListener('click', () => {
      element.hidden = true;
    });
    element.appendChild(close);
    return;
  }
  hideTimer = setTimeout(() => {
    element.hidden = true;
    hideTimer = null;
  }, GTOAST_HIDE_MS);
};
